import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { farmOutAPI, farmOutError } from '../../services/farmOutApi';
import FarmOutCard from './FarmOutCard';

export default function FarmOutComposer({
  reservationId,
}: {
  reservationId: string;
}) {
  const client = useQueryClient();
  const context = useQuery(
    ['farm-out-context', reservationId],
    () => farmOutAPI.context(reservationId),
    { retry: false }
  );
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState({
    pickupArea: '',
    dropoffArea: '',
    requirements: '',
    publicNotes: '',
    amount: '',
    currency: 'USD',
    includesTolls: false,
    includesParking: false,
    includesGratuity: false,
    includedWaitMinutes: '0',
    pricingNotes: '',
  });
  const [review, setReview] = useState(false);
  const mutation = useMutation(
    () =>
      farmOutAPI.publish({
        pickupArea: form.pickupArea,
        dropoffArea: form.dropoffArea,
        requirements: form.requirements,
        publicNotes: form.publicNotes,
        currency: form.currency,
        amountCents: Math.round(Number(form.amount) * 100),
        includesTolls: form.includesTolls,
        includesParking: form.includesParking,
        includesGratuity: form.includesGratuity,
        includedWaitMinutes: Number(form.includedWaitMinutes) || 0,
        pricingNotes: form.pricingNotes,
        reservationId,
        requestId,
      }),
    {
      onSuccess: async () => {
        setReview(false);
        setRequestId(crypto.randomUUID());
        await client.invalidateQueries('farm-out-context');
        await client.invalidateQueries('farm-outs');
      },
    }
  );
  if (context.isLoading)
    return <p role="status">Checking reservation eligibility…</p>;
  if (context.isError) return <p role="alert">{farmOutError(context.error)}</p>;
  if (!context.data) return null;
  const details = context.data;
  const change = (key: keyof typeof form, value: string | boolean) => {
    setForm({ ...form, [key]: value });
    setRequestId(crypto.randomUUID());
    mutation.reset();
  };
  return (
    <section className="space-y-4" aria-label="Offer reservation">
      <h2 className="text-xl font-semibold">
        Reservation {details.reservationRef}
      </h2>
      <p className="text-sm text-on-surface-variant">
        Pickup {new Date(details.startAt).toLocaleString()} · Ends{' '}
        {new Date(details.endAt).toLocaleString()} (your local time)
      </p>
      {details.offer && <FarmOutCard offer={details.offer} />}
      {!details.eligible ? (
        <p className="rounded-lg bg-surface-container-high p-4">
          {details.reason}
        </p>
      ) : (
        <form
          className="admin-subpanel rounded-card p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (review) mutation.mutate();
            else setReview(true);
          }}
        >
          <h3 className="font-semibold">
            {review
              ? 'Review the public offer'
              : details.offer
              ? 'Create a fresh offer'
              : 'Offer this reservation'}
          </h3>
          <p className="text-sm">
            Other approved operators see only the areas and terms you enter
            below. Use city or airport areas; keep passenger names, exact
            addresses, and billing details out of these fields.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              Pickup area
              <input
                className="input-field"
                required
                minLength={2}
                maxLength={160}
                disabled={review}
                value={form.pickupArea}
                onChange={(e) => change('pickupArea', e.target.value)}
                placeholder="e.g. DFW Airport"
              />
            </label>
            <label>
              Drop-off area
              <input
                className="input-field"
                required
                minLength={2}
                maxLength={160}
                disabled={review}
                value={form.dropoffArea}
                onChange={(e) => change('dropoffArea', e.target.value)}
                placeholder="e.g. Downtown Dallas"
              />
            </label>
            <label>
              Fixed offer amount
              <input
                className="input-field"
                type="number"
                min="0.01"
                max="1000000"
                step="0.01"
                required
                disabled={review}
                value={form.amount}
                onChange={(e) => change('amount', e.target.value)}
              />
            </label>
            <label>
              Currency
              <select
                className="input-field"
                disabled={review}
                value={form.currency}
                onChange={(e) => change('currency', e.target.value)}
              >
                {['USD', 'CAD', 'EUR', 'GBP'].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            Vehicle and service requirements
            <textarea
              className="input-field"
              required
              minLength={2}
              maxLength={300}
              disabled={review}
              value={form.requirements}
              onChange={(e) => change('requirements', e.target.value)}
              placeholder={
                details.requirements || 'e.g. SUV, 4 passengers, 3 bags'
              }
            />
          </label>
          <label className="block">
            Public operator notes
            <textarea
              className="input-field"
              maxLength={1000}
              disabled={review}
              value={form.publicNotes}
              onChange={(e) => change('publicNotes', e.target.value)}
            />
          </label>
          <fieldset className="space-y-3">
            <legend className="font-semibold">Amount includes</legend>
            <p className="text-sm text-on-surface-variant">
              Base reservation service is always included. Select any variable
              items included in the listed amount.
            </p>
            <div className="flex flex-wrap gap-4">
              {[
                ['includesTolls', 'Tolls'],
                ['includesParking', 'Parking and airport fees'],
                ['includesGratuity', 'Gratuity'],
              ].map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    disabled={review}
                    checked={Boolean(form[key as keyof typeof form])}
                    onChange={(event) =>
                      change(key as keyof typeof form, event.target.checked)
                    }
                  />{' '}
                  {label}
                </label>
              ))}
            </div>
            <label className="block max-w-xs">
              Included waiting time (minutes)
              <input
                className="input-field"
                type="number"
                min="0"
                max="1440"
                step="1"
                disabled={review}
                value={form.includedWaitMinutes}
                onChange={(event) =>
                  change('includedWaitMinutes', event.target.value)
                }
              />
            </label>
            <label className="block">
              Additional price terms
              <textarea
                className="input-field"
                maxLength={1000}
                disabled={review}
                value={form.pricingNotes}
                onChange={(event) => change('pricingNotes', event.target.value)}
              />
            </label>
            <p className="text-sm text-on-surface-variant">
              Anything not listed as included is excluded. MODAL does not
              calculate or charge later extras for this agreement.
            </p>
          </fieldset>
          {review && (
            <p className="rounded-lg bg-surface-container-low p-3 text-sm">
              Publishing locks this reservation’s route and local assignments
              until you withdraw the offer. Interest alone does not assign a
              job. Providers may propose another amount, and you retain final
              confirmation. Payments are arranged between operators.
            </p>
          )}
          {mutation.isError && (
            <p role="alert" className="text-red-700">
              {farmOutError(mutation.error)}
            </p>
          )}
          <div className="flex gap-2">
            <button className="btn-primary" disabled={mutation.isLoading}>
              {mutation.isLoading
                ? 'Publishing…'
                : review
                ? 'Publish offer'
                : 'Review offer'}
            </button>
            {review && (
              <button
                type="button"
                className="btn-secondary"
                disabled={mutation.isLoading}
                onClick={() => setReview(false)}
              >
                Edit offer
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
